import React, { useState } from 'react';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

// MUI
import CloseIcon from '@material-ui/icons/Close';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import IconButton from '@material-ui/core/IconButton';
import InfoIcon from '@material-ui/icons/Info';
import ShoppingCartIcon from '@material-ui/icons/ShoppingCart';

// app: actions
import { openModal } from 'actions/modals';

// app: components
import Button from 'components/Button';

// app modules: hooks
import useFeatureToggle from 'hooks/useFeatureToggle';

// app: services
import { isApp } from 'services/environment';
import { openWindow } from 'services/inAppBrowser';
import history from 'services/history';
import route from 'services/route';
import { showHelpArticle, ARTICLES } from 'services/intercom';

// app modules: constants
import { MODALS } from 'constants/view';

function BuyBookButton(props) {
  const { alacarte, service, url, compact, indexable, isbn, signature_ } = props;

  const dispatch = useDispatch();
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [dialogContent, setDialogContent] = useState(null);
  const owned = useSelector(state => state.user.owned_books?.includes(isbn));
  const hasSubscription = useSelector(state => !!state.user.subscription);
  const subscriptionIsActive = useSelector(state => !!state.user.subscription?.is_valid);
  const displayType = useSelector(state => state.dimensions.displayType);

  const [alacarteEnabled] = useFeatureToggle('alacarte');

  const handleBuyPrintClick = () => {
    setMenuAnchorEl(null);
    if (isApp()) {
      openWindow(url, '_blank');
    } else {
      window.location = url;
    }
  };

  const handleAppMoreInfoClick = () => {
    showHelpArticle(ARTICLES.accessingAlacarte);
    setDialogContent(null);
  };

  const handleBuyAlacarteClick = () => {
    setMenuAnchorEl(null);
    if (!isApp()) {
      if (alacarteEnabled && alacarte) {
        history.push(route.alacartePurchase(isbn, signature_));
      } else if (indexable && !hasSubscription) {
        history.push('/subscribe', { returnTo: history.location });
      }
    } else if (!alacarte && indexable && (!hasSubscription || !subscriptionIsActive)) {
      import('components/InAppPaymentInfo/InAppPaymentInfo.jsx' /* webpackChunkName: "in-app-payment", webpackMode: "lazy" */).then(
        module => {
          dispatch(
            openModal({
              name: MODALS.IN_APP_PAYMENT,
              content: module.default,
            }),
          );
        },
      );
    } else {
      setDialogContent(
        <React.Fragment>
          <p className="margin-top-5">
            <strong>Visit app.ckbk.com to purchase </strong>
          </p>
          <p>
            <button className="link link--energized" onClick={handleAppMoreInfoClick}>
              more info
            </button>
          </p>
        </React.Fragment>,
      );
    }
  };

  const handleButtonClick = event => {
    if (
      (alacarteEnabled && alacarte) ||
      (indexable && (!hasSubscription || !subscriptionIsActive))
    ) {
      setMenuAnchorEl(event.currentTarget);
    } else {
      handleBuyPrintClick();
    }
  };

  const handleClose = () => {
    setMenuAnchorEl(null);
  };

  // Keep Apple reviewers happy by clearly stating that we are selling a physical object
  let caption = 'Buy this book';
  if (displayType === 'mobile' && compact) {
    // We'll need to squeeze this if there is a Free EBook button.
    caption = 'Buy Book';
  }

  if (service === 'fishpond') {
    return (
      <Button
        onClick={handleButtonClick}
        target="_system"
        themeName="energized"
        size="large"
        title={caption}
        className="book__action-button"
      >
        <span className="book__action-text">{caption}</span>
      </Button>
    );
  }

  let getOnCkbkOption = null;
  if (alacarteEnabled && alacarte) {
    getOnCkbkOption = owned ? (
      <MenuItem disabled>
        <img src="images/ckbk-book.png" alt="" width={40} />&nbsp;You already own this book
      </MenuItem>
    ) : (
      <MenuItem onClick={handleBuyAlacarteClick}>
        <img src="images/ckbk-book.png" alt="" width={40} />&nbsp;Buy à la carte digital
      </MenuItem>
    );
  } else if (indexable && !hasSubscription) {
    getOnCkbkOption = (
      <MenuItem onClick={handleBuyAlacarteClick}>
        <img src="images/ckbk-book.png" alt="" width={40} />&nbsp;Online access with ckbk 14 day
        free trial
      </MenuItem>
    );
  } else if (hasSubscription && !subscriptionIsActive) {
    getOnCkbkOption = (
      <MenuItem onClick={handleBuyAlacarteClick}>
        <img src="images/ckbk-book.png" alt="" width={40} />&nbsp;Online access with ckbk
      </MenuItem>
    );
  }
  return (
    <React.Fragment>
      <Button
        onClick={handleButtonClick}
        target="_system"
        themeType="clear"
        themeName="energized"
        size="large"
        title={caption}
        className="book__action-button"
      >
        <ShoppingCartIcon style={{ height: 20, width: 20, marginRight: 5 }} />
        <span className="book__action-text">{caption}</span>
      </Button>
      <Menu anchorEl={menuAnchorEl} keepMounted open={Boolean(menuAnchorEl)} onClose={handleClose}>
        {getOnCkbkOption}
        {url ? (
          <MenuItem onClick={handleBuyPrintClick}>
            <div style={{ height: 15, width: 40, textAlign: 'center', verticalAlign: 'center' }}>
              {service === 'fishpond' ? null : (
                <img src="images/amazon.png" alt="" width={40} className="margin-top-5" />
              )}
            </div>&nbsp;Buy print
          </MenuItem>
        ) : null}
      </Menu>
      <Dialog maxWidth="sm" open={!!dialogContent} onClose={() => setDialogContent(null)}>
        <DialogActions>
          <IconButton onClick={() => setDialogContent(null)} aria-label="Close" color="inherit">
            <CloseIcon />
          </IconButton>
        </DialogActions>
        <div className="book__dialog-content">
          <div>
            <InfoIcon fontSize="large" />
          </div>
          <div>{dialogContent}</div>
        </div>
      </Dialog>
    </React.Fragment>
  );
}

BuyBookButton.propTypes = {
  alacarte: PropTypes.bool,
  compact: PropTypes.bool,
  indexable: PropTypes.bool,
  isbn: PropTypes.string,
  service: PropTypes.string,
  signature_: PropTypes.string,
  url: PropTypes.string,
};

BuyBookButton.defaultProps = {
  alacarte: false,
  compact: false,
  indexable: false,
  isbn: null,
  service: null,
  signature_: null,
  url: null,
};

export default BuyBookButton;
